import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PenLine, Stamp, Upload, Trash2 } from "lucide-react";

function fileToDataUrl(file, maxW = 600, maxH = 240) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const k = Math.min(1, maxW / img.width, maxH / img.height);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/png"));
    };
    img.onerror = () => reject(new Error("Immagine non leggibile"));
    img.src = url;
  });
}

function ImgUpload({ value, onChange, label, hint, testid, h = "h-24" }) {
  const ref = useRef();
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { onChange(await fileToDataUrl(f)); } catch (err) { toast.error(err.message); }
    e.target.value = "";
  };
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className={`${h} border border-dashed border-slate-300 rounded-lg bg-slate-50 flex items-center justify-center overflow-hidden`} data-testid={`${testid}-preview`}>
        {value ? <img src={value} alt={label} className="max-h-full max-w-full object-contain p-1" /> : <span className="text-xs text-slate-400">{hint}</span>}
      </div>
      <div className="flex gap-2">
        <input ref={ref} type="file" accept="image/png,image/jpeg" className="hidden" onChange={pick} data-testid={`${testid}-file`} />
        <Button type="button" size="sm" variant="outline" onClick={() => ref.current.click()} data-testid={`${testid}-upload`}><Upload className="h-3.5 w-3.5 mr-1" /> Carica immagine</Button>
        {value && <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => onChange("")} data-testid={`${testid}-remove`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Rimuovi</Button>}
      </div>
    </div>
  );
}

export function ProfiloFirma() {
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data });
  const { data: tb } = useQuery({ queryKey: ["timbro"], queryFn: async () => (await api.get("/impostazioni/timbro")).data, enabled: isAdmin });
  const [f, setF] = useState({ telefono: "", email_contatto: "", firma: "" });
  const [timbro, setTimbro] = useState("");
  useEffect(() => { if (me) setF({ telefono: me.telefono || "", email_contatto: me.email_contatto || "", firma: me.firma || "" }); }, [me]);
  useEffect(() => { if (tb) setTimbro(tb.timbro || ""); }, [tb]);

  const save = useMutation({
    mutationFn: async () => {
      await api.put("/auth/profilo", f);
      if (isAdmin && timbro !== (tb?.timbro || "")) await api.put("/impostazioni/timbro", { timbro });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["me"] }); qc.invalidateQueries({ queryKey: ["timbro"] }); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Profilo salvato"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4" data-testid="profilo-firma">
      <div>
        <h2 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><PenLine className="h-4 w-4 text-primary" /> Il mio profilo e firma</h2>
        <p className="text-sm text-slate-500">Le email a clienti e fornitori partono dalla casella aziendale ma riportano i tuoi contatti; le risposte arrivano alla tua email. La firma compare sui preventivi PDF che invii.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nome</Label><Input value={user?.name || ""} disabled /></div>
          <div className="space-y-1.5"><Label>Telefono</Label><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} placeholder="es. 335 1234567" data-testid="profilo-telefono" /></div>
          <div className="space-y-1.5"><Label>Email per le risposte</Label><Input type="email" value={f.email_contatto} onChange={(e) => setF({ ...f, email_contatto: e.target.value })} placeholder={user?.email} data-testid="profilo-email-contatto" /><p className="text-[11px] text-slate-400">Vuoto = usa l'email di accesso ({user?.email}).</p></div>
        </div>
        <ImgUpload label="La mia firma (PNG/JPG, sfondo bianco)" hint="Nessuna firma caricata" value={f.firma} onChange={(v) => setF({ ...f, firma: v })} testid="profilo-firma" />
        {isAdmin && <ImgUpload label="Timbro aziendale (solo admin)" hint="Nessun timbro caricato" value={timbro} onChange={setTimbro} testid="timbro" />}
      </div>
      <div className="flex justify-end"><Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="profilo-salva"><Stamp className="h-4 w-4 mr-1.5" /> Salva profilo</Button></div>
    </div>
  );
}
